import React from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import createPlotlyComponent from 'react-plotly.js/factory'
import Plotly from 'plotly.js-basic-dist-min'

const Plot = createPlotlyComponent(Plotly)

const eurFormatter = new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' })
const pctFormatter = new Intl.NumberFormat('fi-FI', { style: 'percent', maximumFractionDigits: 1 })

const CURRENT_COLOR = '#0b8043'
const PREVIOUS_COLOR = '#ea4335'

// data: { months: ['Tam', 'Hel', ...], current: [n,...], previous: [n,...], currentYearLabel, previousYearLabel }
const YtdCumulativeCard = ({ title = 'Kumuloitunut myynti tilikauden alusta', data, loading }) => {
  const months = (data && data.months) || []
  const current = (data && data.current) || []
  const previous = (data && data.previous) || []
  const currentYearLabel = (data && data.currentYearLabel) || ''
  const previousYearLabel = (data && data.previousYearLabel) || ''
  const hasData = current.some((v) => v > 0) || previous.some((v) => v > 0)

  const currentLatest = current.length > 0 ? current[current.length - 1] : 0
  const previousAtSameMonth = previous.length >= current.length ? previous[current.length - 1] : 0
  const delta = currentLatest - (previousAtSameMonth || 0)
  const pct = previousAtSameMonth > 0 ? delta / previousAtSameMonth : null
  const direction = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'
  const color = direction === 'up' ? '#28a745' : direction === 'down' ? '#dc3545' : '#6c757d'
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : ''

  const traces = [
    {
      x: months,
      y: previous,
      name: previousYearLabel,
      type: 'scatter',
      mode: 'lines+markers',
      line: { color: PREVIOUS_COLOR, width: 2, dash: 'dot' },
      marker: { size: 5 },
      hovertemplate: `%{x}: %{y:,.2f} €<extra>${previousYearLabel}</extra>`,
    },
    {
      x: months,
      y: current,
      name: currentYearLabel,
      type: 'scatter',
      mode: 'lines+markers',
      line: { color: CURRENT_COLOR, width: 2 },
      marker: { size: 6 },
      hovertemplate: `%{x}: %{y:,.2f} €<extra>${currentYearLabel}</extra>`,
    },
  ]

  const layout = {
    autosize: true,
    height: 240,
    margin: { l: 60, r: 12, t: 4, b: 30 },
    showlegend: true,
    legend: { orientation: 'h', x: 0, y: 1.18, font: { size: 11 } },
    xaxis: { tickfont: { size: 11, color: '#5f6368' } },
    yaxis: { tickfont: { size: 11, color: '#5f6368' }, gridcolor: 'rgba(0,0,0,0.06)', tickformat: ',.0f', ticksuffix: ' €' },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    separators: ', ',
    hoverlabel: { bgcolor: '#fff' },
  }

  const config = { displayModeBar: false, responsive: true }

  return (
    <Card sx={{ borderRadius: 2, height: '100%' }}>
      <CardContent sx={{ p: '12px 14px', '&:last-child': { pb: '12px' } }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant='caption' sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3 }}>
            {title}
          </Typography>
          {!loading && hasData && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Box sx={{ fontSize: 18, fontWeight: 600 }}>{eurFormatter.format(currentLatest)}</Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color, fontSize: 12 }}>
                {direction === 'up' && <ArrowUpwardIcon sx={{ fontSize: 14 }} />}
                {direction === 'down' && <ArrowDownwardIcon sx={{ fontSize: 14 }} />}
                <span>
                  {pct === null ? '—' : `${sign}${pctFormatter.format(Math.abs(pct))}`}
                  {' '}
                  ({sign}{eurFormatter.format(Math.abs(delta))})
                </span>
                <Box component='span' sx={{ color: 'text.secondary', ml: 0.5 }}>vs viime vuosi</Box>
              </Box>
            </Box>
          )}
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
            <CircularProgress size={20} />
          </Box>
        ) : !hasData ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240, color: 'text.secondary', fontSize: 12 }}>
            Ei dataa
          </Box>
        ) : (
          <Plot data={traces} layout={layout} config={config} style={{ width: '100%', height: 240 }} useResizeHandler />
        )}
      </CardContent>
    </Card>
  )
}

export default YtdCumulativeCard
